import {SPICE_CHUNK_SIZE, SPICE_PREDICT_OUT_LEN, SPICE_UNIT} from "./pitch.const";



/**
 * 파형을 분석해서 진폭을 반환한다.
 *
 * SPICE 알고리즘에 맞게 수정함. (추정치)
 * SPICE는 한번 predict 하는 (단위 / 512) + 1의 배열 길이를 반환한다.
 * 예를 들어 한번에 처리하는 단위가 10240 이면 (10240/512)+1 = 21 개를 반환함.
 * 시작과 끝은 256개 범위로 계산하고 중간은 512개 범위로 계산한다.
 */
export function getWavAmplitude(arrWav: Array<number>): Array<number> {
  const INITIAL_FINAL_SEGMENT_SIZE = 256;

  const ret: Array<number> = [];

  for (let i = 0; i < arrWav.length; i += SPICE_CHUNK_SIZE) {
    const chunkEnd = Math.min(i + SPICE_CHUNK_SIZE, arrWav.length);

    // Initial segment
    let segmentEnd = Math.min(i + INITIAL_FINAL_SEGMENT_SIZE, chunkEnd);
    let maxVal = Number.MIN_VALUE;
    for (let j = i; j < segmentEnd; j++) {
      maxVal = Math.max(maxVal, Math.abs(arrWav[j]));
    }
    ret.push(maxVal);

    // Middle segments
    // 512개가  안돼도 추가한다.
    for (let j = segmentEnd; j < chunkEnd - INITIAL_FINAL_SEGMENT_SIZE; j += SPICE_UNIT) {
      let segmentMax = Number.MIN_VALUE;
      segmentEnd = Math.min(j + SPICE_UNIT, chunkEnd - INITIAL_FINAL_SEGMENT_SIZE);
      for (let k = j; k < segmentEnd; k++) {
        segmentMax = Math.max(segmentMax, Math.abs(arrWav[k]));
      }
      ret.push(segmentMax);
    }

    // Final segment
    // 없어도 0을 추가한다. (SPICE결과 값과 출력 배열의 갯수를 맞추기 위해서)
    maxVal = 0;
    segmentEnd = chunkEnd - INITIAL_FINAL_SEGMENT_SIZE;
    for (let j = segmentEnd; j < chunkEnd; j++) {
      maxVal = Math.max(maxVal, Math.abs(arrWav[j]));
    }
    ret.push(maxVal);
  }
  return ret;
}


/**
 * 그래프 양쪽의 빈 공간을 제거하기위해서 양쪽의 NaN을 모두 잘라낸다.
 * @param data
 */
export function trimNaN(data: Array<number>): Array<number> {
  // let firstIdx = data.findIndex(v => !isNaN(v));
  // firstIdx = firstIdx === -1 ? 0 : firstIdx;
  let firstIdx = 0;
  const len = data.length;
  for (; firstIdx < len - 2; ++firstIdx) {
    if (!isNaN(data[firstIdx]) && !isNaN(data[firstIdx + 1]) && !isNaN(data[firstIdx + 2])) { break; }
    ++firstIdx;
  }

  let lastIdx = len;
  // for (; lastIdx > firstIdx + 2; --lastIdx) {
  // if (!isNaN(data[lastIdx - 1]) && !isNaN(data[lastIdx - 2]) && !isNaN(data[lastIdx - 3])) { break; }
  for (; lastIdx > firstIdx + 1; --lastIdx) {
    if (!isNaN(data[lastIdx - 1]) && !isNaN(data[lastIdx - 2])) { break; }
  }

  // console.log('slice = ', firstIdx, lastIdx, data);
  const ret = data.slice(firstIdx, lastIdx);
  return ret;
}


export async function loadAudioFromBlob(blob: Blob): Promise<ArrayBuffer>
{
  let fileReader = new FileReader();
  const loadFile = new Promise<ArrayBuffer>((resolve, reject) => {
    fileReader.onerror = () => {
      fileReader.abort();
      reject(new DOMException('녹음한 파일을 읽는 중 오류가 발생했습니다.'));
    };

    fileReader.onload = () => {
      resolve(fileReader.result as ArrayBuffer);
    };

    fileReader.readAsArrayBuffer(blob);
  });

  const arrayBuffer: ArrayBuffer = await loadFile;
  return arrayBuffer;
}


/**
 * 소리 데이터 정규화
 * Peak normalization 방법 사용
 *
 * @param audioBuffer
 */
export function normalizeAudioBuffer(audioBuffer: Float32Array): Float32Array {
  let maxSample = -Infinity;

  // 최대값 찾기
  for (let i = 0; i < audioBuffer.length; i++) {
    if (Math.abs(audioBuffer[i]) > maxSample) {
      maxSample = Math.abs(audioBuffer[i]);
    }
  }

  // 최대값으로 나누기
  const normalizedBuffer = new Float32Array(audioBuffer.length);
  for (let i = 0; i < audioBuffer.length; i++) {
    normalizedBuffer[i] = audioBuffer[i] / maxSample;
  }

  return normalizedBuffer;
}


/**
 * RMS (Root Mean Square) 정규화
 *
 * @param audioBuffer
 * @param targetRMS
 */
export function rmsNormalization(audioBuffer: Float32Array, targetRMS: number = 0.5): Float32Array {
  let sumSquare = 0;
  for (let i = 0; i < audioBuffer.length; i++) {
    sumSquare += audioBuffer[i] * audioBuffer[i];
  }
  const rms = Math.sqrt(sumSquare / audioBuffer.length);

  const normalizedBuffer = new Float32Array(audioBuffer.length);
  for (let i = 0; i < audioBuffer.length; i++) {
    normalizedBuffer[i] = (audioBuffer[i] / rms) * targetRMS;
  }

  return normalizedBuffer;
}
